import { useEffect, useState } from 'react';
import { isPolicyReviewInProgress } from '@shared/consumerPlan';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadFileToS3 } from '@/util/uploadFileToBucket';
import { consumerApi, consumerPlanKeys } from '../api/consumer-api';
import { consumerStepPath } from '../lib/paths';
import { isAwaitingPayment, useConsumerPlan } from './use-consumer-plan';
import { useConsumerFlow } from './use-consumer-flow';
import { useSelfFundAccess } from './use-self-fund-access';
import { usePolicyReviewDraft, usePolicyReviewDraftActions } from '../stores/policy-review-draft-store';

const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
type AllowedType = (typeof ALLOWED_TYPES)[number];

function isAllowedType(type: string): type is AllowedType {
  return (ALLOWED_TYPES as readonly string[]).includes(type);
}

/** Uploads policy documents straight to storage and keeps them on the review draft. */
export function usePolicyReviewUpload(clientId: string) {
  const { addDocument } = usePolicyReviewDraftActions();
  const [error, setError] = useState<string | null>(null);
  // Files still on their way, shown with a spinner until each one lands on the draft.
  const [pendingNames, setPendingNames] = useState<string[]>([]);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const accepted = files.filter(file => isAllowedType(file.type));
      setError(accepted.length < files.length ? 'Please use PDF, JPG or PNG files. Some files were not added.' : null);
      setPendingNames(accepted.map(file => file.name));

      for (const file of accepted) {
        const { url, document } = await consumerApi.getPolicyReviewUploadUrl(clientId, {
          fileName: file.name,
          contentType: file.type,
        });
        await uploadFileToS3(url, file, file.type as AllowedType);
        addDocument(document);
        setPendingNames(names => names.slice(1));
      }
    },
    onError: () => setError('We couldn’t upload a file. Please try again.'),
    onSettled: () => setPendingNames([]),
  });

  return { uploadFiles: upload.mutate, isUploading: upload.isPending, pendingNames, error };
}

/**
 * Whether the review is included with the consumer's self-funding access instead of paid for. The
 * consumer's own session carries their access; an advisor or wizard reads the client's from the plan.
 * Either way the server decides from the client's record when the review is sent.
 */
export function useIsPolicyReviewIncluded(clientId: string): boolean {
  const { access, isConsumerSession } = useSelfFundAccess();
  const { hasSelfFundAccess } = useConsumerPlan(clientId);
  return isConsumerSession ? access === 'active' : hasSelfFundAccess;
}

/**
 * Sends the review. A paid one opens Stripe checkout, which brings the consumer back to the status
 * screen; one included with self-funding access is already confirmed, so it goes straight there.
 */
export function usePolicyReviewCheckout(clientId: string) {
  const { reviewType, documents, responseDeadline } = usePolicyReviewDraft();
  const { goTo } = useConsumerFlow(clientId);
  const queryClient = useQueryClient();

  const checkout = useMutation({
    mutationFn: () =>
      consumerApi.startPolicyReviewCheckout(clientId, {
        reviewType: reviewType ?? 'policy',
        documents,
        responseDeadline: reviewType === 'rate' && responseDeadline ? responseDeadline : null,
        successPath: consumerStepPath(clientId, 'review-status'),
        cancelPath: consumerStepPath(clientId, 'review-checkout'),
      }),
    onSuccess: async result => {
      if (!result.isIncluded) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      // The status screen reads the plan, which is cached until something changes it.
      await queryClient.invalidateQueries({ queryKey: consumerPlanKeys.detail(clientId), refetchType: 'all' });
      goTo('review-status');
    },
  });

  return { startCheckout: checkout.mutate, isStarting: checkout.isPending, isError: checkout.isError };
}

export type PolicyReviewPayment = 'paid' | 'confirming' | 'unpaid';

function policyReviewPayment(review: ConsumerPolicyReview | null): PolicyReviewPayment {
  if (review?.status === 'paid') return 'paid';
  if (isAwaitingPayment(review ?? undefined)) return 'confirming';
  return 'unpaid';
}

/** The latest review and where its payment stands; the finished draft is cleared once it's paid. */
export function usePolicyReviewStatus(clientId: string) {
  const { plan, policyReviews } = useConsumerPlan(clientId, { pollWhileAwaitingPayment: true });
  const { reset } = usePolicyReviewDraftActions();
  const review = policyReviews[0] ?? null;
  const payment = policyReviewPayment(review);

  useEffect(() => {
    if (payment === 'paid') reset();
  }, [payment, reset]);

  return { plan, review, payment };
}

/** The consumer's review the team is still working on, if any. */
export function usePolicyReviewInProgress(clientId: string): ConsumerPolicyReview | null {
  const { policyReviews } = useConsumerPlan(clientId);
  return policyReviews.find(review => isPolicyReviewInProgress(review)) ?? null;
}
